"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface AnimatedTabPanelProps {
  activeKey: string;
  children: ReactNode;
  className?: string;
  duration?: number;
  ease?: [number, number, number, number];
  blur?: number;
  y?: number;
  /** "popLayout" crossfades old and new without an empty frame between them. */
  mode?: "wait" | "popLayout" | "sync";
  layoutMode?: boolean | "size" | "position";
  layoutDuration?: number;
}

export function AnimatedTabPanel({
  activeKey,
  children,
  className,
  duration = 0.28,
  ease = [0.4, 0, 0.2, 1],
  blur = 6,
  y = 6,
  mode = "popLayout",
  layoutMode = true,
  layoutDuration = 0.32,
}: AnimatedTabPanelProps) {
  const reduceMotion = useReducedMotion() ?? false;

  const motionY = reduceMotion ? 0 : y;
  const motionBlur = reduceMotion ? 0 : blur;
  const motionDuration = reduceMotion ? 0 : duration;

  return (
    <motion.div
      layout={reduceMotion ? false : layoutMode}
      transition={{
        layout: { duration: reduceMotion ? 0 : layoutDuration, ease },
      }}
      className={cn("relative", className)}
    >
      <AnimatePresence mode={mode} initial={false}>
        <motion.div
          key={activeKey}
          initial={{ opacity: 0, y: motionY, filter: `blur(${motionBlur}px)` }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -motionY, filter: `blur(${motionBlur}px)` }}
          transition={{ duration: motionDuration, ease }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
